/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import API from "../lib/api";
import { Section } from "../components/Common";
import { PractitionerCard } from "../components/PractitionerRecommendations";
import { toast } from "sonner";
import { COUNTRY_LIST } from "../lib/countries";
import { Heart, ArrowRight, Briefcase, ShieldCheck } from "lucide-react";

export default function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading, logout, refresh } = useAuth();
  const [doshaResults, setDoshaResults] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [savedPractitioners, setSavedPractitioners] = useState([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  // Welcome toast after Google OAuth redirect (?welcome=1)
  useEffect(() => {
    if (searchParams.get("welcome") === "1" && user) {
      toast.success(t("auth.welcome_toast", "Welcome to DONiTSH!T"));
      const next = new URLSearchParams(searchParams);
      next.delete("welcome");
      setSearchParams(next, { replace: true });
    }
  }, [user, searchParams, setSearchParams, t]);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setCountry(user.country || "");
    Promise.all([
      API.get("/dosha/my-results").catch(() => ({ data: { items: [] } })),
      API.get("/coach/my-conversations").catch(() => ({ data: { items: [] } })),
    ]).then(([d, c]) => { setDoshaResults(d.data.items || []); setConversations(c.data.items || []); });
  }, [user]);

  // Saved practitioners (Step 5E)
  useEffect(() => {
    if (!user) { setSavedPractitioners([]); setSavedLoading(false); return; }
    setSavedLoading(true);
    API.get("/marketplace/favorites")
      .then(({ data }) => setSavedPractitioners(data.items || []))
      .catch(() => setSavedPractitioners([]))
      .finally(() => setSavedLoading(false));
  }, [user]);

  const removeFavorite = async (b) => {
    const prev = savedPractitioners;
    setSavedPractitioners((items) => items.filter((x) => x.business_id !== b.business_id));
    try {
      await API.delete(`/marketplace/favorites/${b.business_id}`);
      toast.success(t("marketplace.removed_from_saved", "Removed from saved"));
    } catch {
      setSavedPractitioners(prev);
      toast.error(t("marketplace.fav_error", "Could not update saved practitioners"));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.patch("/auth/me", { name, country });
      await refresh();
      toast.success(t("common.saved"));
    } catch (e) { toast.error(t("common.could_not_save")); } finally { setSaving(false); }
  };

  if (loading || !user) return <div className="app-container py-20 text-white/70">{t("common.loading")}</div>;

  return (
    <div data-testid="page-profile">
      <Section eyebrow={t("profile.account_eyebrow")} title={user.name || user.email} subtitle={user.email}>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="card-surface p-6" data-testid="profile-form">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3">{t("profile.settings")}</div>
            <label className="text-sm text-white/70">{t("auth.name")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="profile-name" />
            <label className="text-sm text-white/70 mt-3 block">{t("profile.country")}</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full mt-1 bg-[#0F1116] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="profile-country">
              <option value="">{t("profile.country_placeholder")}</option>
              {COUNTRY_LIST.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
            </select>
            <button onClick={save} disabled={saving} className="btn-primary text-sm mt-4" data-testid="profile-save">{saving ? "…" : t("common.save")}</button>
            <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost text-sm mt-3 block" data-testid="profile-logout">{t("nav.logout")}</button>
          </div>
          <div className="card-surface p-6" data-testid="profile-dosha">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3">{t("profile.saved_dosha_title")}</div>
            {doshaResults.length === 0 ? (
              <div className="text-white/60 text-sm">{t("profile.saved_dosha_empty_prefix")} <Link to="/dosha" className="underline">{t("profile.take_the_test")}</Link>.</div>
            ) : (
              <ul className="space-y-2">
                {doshaResults.map((r) => (
                  <li key={r.result_id}><Link to={`/dosha/result/${r.result_id}`} className="underline">{r.dosha_type}</Link> <span className="text-white/55 text-xs">{(r.created_at || "").substring(0,10)}</span></li>
                ))}
              </ul>
            )}
          </div>
          <div className="card-surface p-6" data-testid="profile-coach">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3">{t("profile.coach_convos_title")}</div>
            {conversations.length === 0 ? (
              <div className="text-white/60 text-sm">{t("profile.coach_convos_empty_prefix")} <Link to="/ask-donny" className="underline">{t("profile.ask_donny_link")}</Link>.</div>
            ) : (
              <ul className="space-y-2">
                {conversations.map((c) => (
                  <li key={c.conversation_id} className="text-sm text-white/75">{c.last_message?.substring(0,80)}… <span className="text-white/50 text-xs">({c.count})</span></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Section>

      {/* PHASE 6.4 — Admin / Business shortcuts */}
      <Section eyebrow={t("profile.tools_eyebrow")} title={t("profile.tools_title")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-tools">
          <Link to="/marketplace/my-business" className="card-surface p-5 hover:border-[#C8A24A]/40 transition-colors flex items-start gap-3 group" data-testid="profile-my-business">
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/25 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center shrink-0">
              <Briefcase className="w-4 h-4 gold-text" />
            </div>
            <div>
              <div className="font-display text-lg leading-tight group-hover:gold-text transition-colors">{t("profile.my_business_title", "My Business Listings")}</div>
              <p className="text-sm text-white/65 mt-1 leading-relaxed">{t("profile.my_business_sub", "Manage your marketplace listings and propose Member Benefits.")}</p>
            </div>
          </Link>
          <Link to="/business/offers" className="card-surface p-5 hover:border-[#C8A24A]/40 transition-colors flex items-start gap-3 group" data-testid="profile-member-benefits">
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/25 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center shrink-0">
              <Heart className="w-4 h-4 gold-text" />
            </div>
            <div>
              <div className="font-display text-lg leading-tight group-hover:gold-text transition-colors">{t("profile.member_benefits_title", "Member Benefits")}</div>
              <p className="text-sm text-white/65 mt-1 leading-relaxed">{t("profile.member_benefits_sub", "Browse and reveal exclusive offers from partner businesses.")}</p>
            </div>
          </Link>
          {user.is_admin && (
            <Link to="/admin/offers" className="card-surface p-5 hover:border-[#C8A24A]/40 transition-colors flex items-start gap-3 group" data-testid="profile-admin-link">
              <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/40 to-[#7A5F2A]/15 border border-[#C8A24A]/45 grid place-items-center shrink-0">
                <ShieldCheck className="w-4 h-4 gold-text" />
              </div>
              <div>
                <div className="font-display text-lg leading-tight group-hover:gold-text transition-colors">{t("profile.admin_title", "Admin Console")}</div>
                <p className="text-sm text-white/65 mt-1 leading-relaxed">{t("profile.admin_sub", "Review, approve, feature, hide, or expire offers proposed by partner businesses.")}</p>
              </div>
            </Link>
          )}
        </div>
      </Section>

      {/* SAVED PRACTITIONERS — Step 5E */}
      <Section
        eyebrow={t("profile.saved_eyebrow")}
        title={t("profile.saved_practitioners_title")}
        subtitle={t("profile.saved_practitioners_subtitle")}
      >
        {savedLoading ? (
          <div className="text-white/60 text-sm" data-testid="profile-saved-loading">{t("common.loading")}</div>
        ) : savedPractitioners.length === 0 ? (
          <div className="card-surface p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="profile-saved-empty">
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 gold-text" />
              <div className="text-sm text-white/70 max-w-[60ch]">
                {t("profile.saved_practitioners_empty", "You haven't saved any practitioners yet. Browse the Marketplace and tap the heart icon to build your shortlist.")}
              </div>
            </div>
            <Link to="/marketplace" className="btn-primary text-sm inline-flex items-center gap-2 self-start sm:self-auto whitespace-nowrap" data-testid="profile-saved-cta">
              {t("nav.marketplace", "Marketplace")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-saved-grid">
            {savedPractitioners.map((b) => (
              <PractitionerCard
                key={b.business_id}
                b={b}
                isFavorite={true}
                onToggleFavorite={() => removeFavorite(b)}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
